import { MarkdownPreview, Spinner } from '@consigli/facade';
import {
  useDocumentPackageSummary,
  useFindings,
  useGetDashboardDataQuery,
  useGetLocaleQuery,
} from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { ConflictCategory, DocumentPackageSummarySection, Finding } from '@consigli/types';
import { formatEnumToReadableText } from '@consigli/utils';
import { FC, useMemo } from 'react';

import { DeleteSummaryButton } from './delete-summary-button';
import { AccordionItem } from './summary-accordion-item';
import { SummarySection } from './summary-section';
import { CATEGORY_SORT_ORDER, ConflictTypesCount, flattenCategories, sumCounts } from './utils';

interface SummaryTabProps {
  projectId: number;
  packageId: string;
  onNavigateToFindings: () => void;
}

export const SummaryTab: FC<SummaryTabProps> = ({ projectId, packageId, onNavigateToFindings }) => {
  const { data: dashboardData, isLoading: isDashboardLoading } = useGetDashboardDataQuery({
    projectId,
    packageId,
  });
  const { summary, isLoading: isSummaryLoading } = useDocumentPackageSummary(projectId, packageId);
  const { findings, isLoading: isFindingsLoading } = useFindings({
    projectId,
    packageId,
    page: 'all',
  });
  const { isLoading: isLocaleLoading } = useGetLocaleQuery('en');

  const isLoading = isDashboardLoading || isSummaryLoading || isFindingsLoading || isLocaleLoading;

  const findingsById = useMemo<Map<number, Finding>>(() => {
    return new Map(findings.map((f) => [f.id, f]));
  }, [findings]);

  const sectionsByCategory = useMemo<Map<string, DocumentPackageSummarySection[]>>(() => {
    const map = new Map<string, DocumentPackageSummarySection[]>();
    if (!summary) return map;
    for (const section of summary.sections) {
      const existing = map.get(section.categoryType) ?? [];
      map.set(section.categoryType, [...existing, section]);
    }
    return map;
  }, [summary]);

  const allConflictEntries = useMemo<[string, ConflictTypesCount[string]][]>(() => {
    if (!dashboardData?.conflictTypesCount) return [];
    return Object.entries(dashboardData.conflictTypesCount) as [
      string,
      ConflictTypesCount[string],
    ][];
  }, [dashboardData]);

  const riskItems = useMemo(() => {
    const categories = allConflictEntries
      .filter(([key]) => key !== ConflictCategory.INSIGHT)
      .sort(
        ([a], [b]) =>
          (CATEGORY_SORT_ORDER[a as ConflictCategory] ?? 99) -
          (CATEGORY_SORT_ORDER[b as ConflictCategory] ?? 99),
      );
    return flattenCategories(categories);
  }, [allConflictEntries]);

  const insightItems = useMemo(() => {
    const categories = allConflictEntries.filter(([key]) => key === ConflictCategory.INSIGHT);
    return flattenCategories(categories);
  }, [allConflictEntries]);

  if (isLoading) return <Spinner />;

  return (
    <div className="min-h-full px-4 py-2">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold text-gray-600">Summary Overview</h1>
        <DeleteSummaryButton projectId={projectId} packageId={packageId} summary={summary} />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 text-sm text-gray-500 leading-relaxed mb-6">
        {summary?.description ? (
          <MarkdownPreview content={summary.description} />
        ) : (
          <span className="italic">
            The summary of the analysis will appear here once processing is complete.
          </span>
        )}
      </div>

      <SummarySection title="Risk Findings" count={sumCounts(riskItems)}>
        {riskItems.map((item) => (
          <AccordionItem
            key={item.typeId}
            title={formatEnumToReadableText(item.typeId)}
            count={item.count}
            sections={sectionsByCategory.get(item.typeId) ?? []}
            findingsById={findingsById}
            onNavigateToFindings={onNavigateToFindings}
          />
        ))}
      </SummarySection>

      <SummarySection title="Insights" count={sumCounts(insightItems)}>
        {insightItems.map((item) => (
          <AccordionItem
            key={item.typeId}
            title={formatEnumToReadableText(item.typeId)}
            count={item.count}
            sections={sectionsByCategory.get(item.typeId) ?? []}
            findingsById={findingsById}
            onNavigateToFindings={onNavigateToFindings}
          />
        ))}
      </SummarySection>

      <Button
        variant="link"
        size="sm"
        onClick={onNavigateToFindings}
        className="text-gray-500 px-0"
      >
        View all findings →
      </Button>
    </div>
  );
};
